import React from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { Button } from 'reactstrap';
import { Drawer, DrawerControls } from '../../../core/components/Drawer';
import { TemplateType, TemplateTypeLabel } from '../models';

interface AddTemplateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TemplateFormData) => void;
}

export interface TemplateFormData {
  name: string;
  description: string;
  type: string;
  version: string;
}

const TEMPLATE_DRAWER_WIDTH = 50;

const templateTypeOptions = Object.values(TemplateType).map((value) => ({
  value,
  label: TemplateTypeLabel[value],
}));

const AddTemplateDrawer: React.FC<AddTemplateDrawerProps> = ({ isOpen, onClose, onSubmit }) => {
  const intl = useIntl();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TemplateFormData>({
    defaultValues: {
      name: '',
      description: '',
      type: '',
      version: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = (data: TemplateFormData) => {
    onSubmit(data);
    reset();
  };

  return (
    <Drawer
      expanded={isOpen}
      overlay
      expandedWidth={TEMPLATE_DRAWER_WIDTH}
      onBackdropClick={handleClose}
    >
      <div className="add-template-drawer">
        <div className="add-template-drawer__header">
          <div className="add-template-drawer__header-text">
            <h3 className="add-template-drawer__title">
              {intl.formatMessage({ id: 'assets.template.drawerTitle' })}
            </h3>
          </div>
          <DrawerControls closeFunction={handleClose} />
        </div>

        <form className="add-template-drawer__form" onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="add-template-drawer__field">
            <label className="add-template-drawer__label" htmlFor="template-name">
              {intl.formatMessage({ id: 'assets.template.name' })}
              <span className="add-template-drawer__required"> *</span>
            </label>
            <input
              id="template-name"
              type="text"
              className={`add-template-drawer__input ${
                errors.name ? 'add-template-drawer__input--error' : ''
              }`}
              placeholder={intl.formatMessage({ id: 'assets.template.namePlaceholder' })}
              {...register('name', {
                required: intl.formatMessage({ id: 'assets.template.nameRequired' }),
              })}
            />
            {errors.name && (
              <span className="add-template-drawer__error">{errors.name.message}</span>
            )}
          </div>

          <div className="add-template-drawer__field">
            <label className="add-template-drawer__label" htmlFor="template-description">
              {intl.formatMessage({ id: 'assets.template.description' })}
            </label>
            <textarea
              id="template-description"
              className="add-template-drawer__textarea"
              placeholder={intl.formatMessage({ id: 'assets.template.descriptionPlaceholder' })}
              rows={3}
              {...register('description')}
            />
          </div>

          <div className="add-template-drawer__row">
            <div className="add-template-drawer__field add-template-drawer__field--half">
              <label className="add-template-drawer__label" htmlFor="template-type">
                {intl.formatMessage({ id: 'assets.template.type' })}
                <span className="add-template-drawer__required"> *</span>
              </label>
              <select
                id="template-type"
                className={`add-template-drawer__select ${
                  errors.type ? 'add-template-drawer__select--error' : ''
                }`}
                {...register('type', {
                  required: intl.formatMessage({ id: 'assets.template.typeRequired' }),
                })}
              >
                <option value="">
                  {intl.formatMessage({ id: 'assets.template.typePlaceholder' })}
                </option>
                {templateTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.type && (
                <span className="add-template-drawer__error">{errors.type.message}</span>
              )}
            </div>

            <div className="add-template-drawer__field add-template-drawer__field--half">
              <label className="add-template-drawer__label" htmlFor="template-version">
                {intl.formatMessage({ id: 'assets.template.version' })}
              </label>
              <input
                id="template-version"
                type="text"
                className="add-template-drawer__input"
                placeholder={intl.formatMessage({ id: 'assets.template.versionPlaceholder' })}
                {...register('version')}
              />
            </div>
          </div>

          <div className="add-template-drawer__actions">
            <Button type="button" color="secondary" outline onClick={handleClose}>
              {intl.formatMessage({ id: 'assets.actions.cancel' })}
            </Button>
            <Button type="submit" color="primary">
              {intl.formatMessage({ id: 'assets.template.addTemplate' })}
            </Button>
          </div>
        </form>
      </div>
    </Drawer>
  );
};

export default AddTemplateDrawer;
